import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { ShieldAlert, UserCheck, FileText, Lock } from "lucide-react";

const cards = [
  {
    icon: ShieldAlert,
    color: "text-red-600",
    title: "Anti-Money Laundering",
    text:
      "We strictly prohibit the use of our website or services for money laundering, terrorist financing, sanctions evasion, fraud, or any unlawful financial activity.",
  },
  {
    icon: UserCheck,
    color: "text-green-600",
    title: "Customer Verification",
    text:
      "We may conduct Know Your Customer checks and request identity, address, ownership, and transaction-supporting information before or after accepting a customer relationship or payment.",
  },
  {
    icon: FileText,
    color: "text-purple-600",
    title: "Transaction Monitoring",
    text:
      "Transactions and account behavior may be reviewed for unusual patterns, inconsistencies, or indicators of suspicious activity, fraud, abuse, or sanctions-related risk.",
  },
  {
    icon: Lock,
    color: "text-indigo-600",
    title: "Reporting Obligations",
    text:
      "Where legally required, suspicious activity may be escalated, restricted, documented, and reported to competent authorities in accordance with applicable law and compliance obligations.",
  },
];

const sections = [
  {
    title: "Compliance Commitment",
    paragraphs: [
      "Nexora Ventures is committed to maintaining robust controls to prevent money laundering, terrorist financing, fraud, sanctions breaches, and other forms of financial crime. We apply risk-aware procedures designed to support lawful business activity, responsible payment acceptance, and regulatory compliance in the United Arab Emirates.",
      "This AML Policy applies to customer onboarding, website interactions, payment acceptance, service delivery, and any transaction or customer relationship that may create financial crime risk. We may revise our internal controls from time to time to reflect legal requirements, operational developments, and evolving risk indicators.",
    ],
  },
  {
    title: "KYC and Customer Identification",
    paragraphs: [
      "We may implement Know Your Customer procedures before establishing a business relationship, processing a transaction, or continuing service where enhanced verification is warranted. Verification may include obtaining the customer's full name, contact details, billing information, government-issued identification, trade license or company registration documents, proof of address, source-of-funds explanation, and beneficial ownership information where applicable.",
      "For business customers, we may verify the existence of the legal entity, the authority of the person acting on its behalf, the ownership and control structure, and any other information reasonably required to assess legitimacy and compliance risk. We may also request additional documentation where the customer profile, transaction pattern, geography, or commercial purpose raises elevated concerns.",
    ],
  },
  {
    title: "Risk-Based Approach",
    paragraphs: [
      "We apply a risk-based approach when assessing customers, transactions, payment methods, jurisdictions, service types, and delivery channels. Factors that may increase risk include unusual transaction size, mismatch between customer profile and transaction purpose, rapid repeat payments, inconsistent information, opaque ownership structures, high-risk geographies, or conduct suggestive of fraud or sanctions screening concerns.",
      "Based on the identified level of risk, we may apply standard, simplified, or enhanced due diligence measures. Enhanced measures may include deeper identity review, request for supporting commercial documentation, additional management approval, delayed fulfillment pending verification, or rejection of the relationship or transaction.",
    ],
  },
  {
    title: "Transaction Monitoring",
    paragraphs: [
      "We monitor transactions and customer activity to identify red flags such as unusual payment behavior, repeated failed attempts, multiple cards linked to the same activity, inconsistent billing data, abrupt changes in transaction profile, requests lacking a clear commercial rationale, or patterns that may indicate structuring, identity misuse, chargeback abuse, or other suspicious conduct.",
      "Monitoring may be performed through internal review, payment gateway alerts, fraud-screening tools, manual compliance checks, record analysis, or a combination of these methods. We reserve the right to place transactions on hold, request clarification, limit service access, or decline completion of a transaction while a compliance review is underway.",
    ],
  },
  {
    title: "Sanctions and Restricted Activity Screening",
    paragraphs: [
      "We may screen customers, counterparties, business owners, and transactions against applicable sanctions lists, internal risk controls, and restricted-country requirements to the extent reasonably necessary for compliance. We do not knowingly support transactions intended to circumvent legal restrictions, mask beneficial ownership, or facilitate prohibited dealings.",
      "Any customer, transaction, or business relationship that presents sanctions exposure, prohibited-party concerns, or legal incompatibility with UAE requirements may be declined, suspended, or terminated without completing the transaction.",
    ],
  },
  {
    title: "Suspicious Activity Review and Reporting",
    paragraphs: [
      "Where we identify activity that appears suspicious, inconsistent, deceptive, or potentially unlawful, we may conduct internal review and request further evidence to clarify the purpose, source of funds, ownership, or legitimacy of the transaction. We may refuse to process a transaction if the provided information is inadequate, contradictory, or raises unresolved compliance concerns.",
      "Where required by applicable law or competent authority, suspicious transactions or attempted transactions may be documented and reported through the appropriate legal and regulatory channels. We may also preserve relevant records, suspend service access, or cooperate with lawful requests from banks, acquirers, payment processors, or authorities.",
    ],
  },
  {
    title: "Record Keeping",
    paragraphs: [
      "We maintain records relating to customer identification, transaction activity, communications, compliance reviews, and supporting documentation for a period considered appropriate under applicable law, contractual necessity, fraud prevention requirements, and legitimate business interests.",
      "Records may be retained in electronic or documentary form and may be used for audit, dispute resolution, regulatory cooperation, fraud investigations, legal defense, or internal compliance monitoring. Access to such records is restricted to authorized personnel and service providers with a legitimate need to know.",
    ],
  },
  {
    title: "Internal Controls and Escalation",
    paragraphs: [
      "We maintain internal procedures intended to support responsible escalation of compliance concerns, segregation of duties where practical, secure handling of records, and review of higher-risk activity. We may update internal processes, screening logic, or customer verification requirements as part of our ongoing compliance program.",
      "If required for compliance purposes, we may ask customers for clarification, additional identity evidence, commercial documentation, or proof of authorization before proceeding. Failure to cooperate with a legitimate compliance request may result in service delay, transaction rejection, account restriction, or termination of the relationship.",
    ],
  },
  {
    title: "UAE Legal Alignment",
    paragraphs: [
      "This AML Policy is intended to support compliance with applicable anti-money laundering and counter-financial-crime expectations in the United Arab Emirates, together with relevant payment-processing, fraud-prevention, sanctions-screening, and record-retention practices. Nothing in this policy limits our right to take stricter measures where necessary to manage legal, operational, financial, or reputational risk.",
      "Customers are expected to provide truthful information, cooperate with verification procedures, and avoid using our website or services for any activity that is illegal, deceptive, or inconsistent with the stated commercial purpose of the transaction.",
    ],
  },
  {
    title: "Policy Updates",
    paragraphs: [
      "We may amend this AML Policy from time to time to reflect changes in law, risk environment, payment requirements, service model, or internal compliance procedures. Updated versions will be published on this website and will become effective from the date of publication unless otherwise stated.",
    ],
  },
];

function AmlPolicy() {
  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-white text-gray-900">
      <Navbar />

      <section className="bg-gradient-to-r from-red-600 via-pink-600 to-orange-500 text-white py-20 text-center">
        <h1 className="text-5xl font-extrabold mb-6">AML Policy</h1>
        <p className="text-lg text-gray-200 max-w-2xl mx-auto">
          Nexora Ventures is committed to preventing money laundering, fraud,
          terrorist financing, and other illegal financial activities through
          strong compliance controls.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 space-y-14">
        <div className="grid md:grid-cols-2 gap-8">
          {cards.map(({ icon: Icon, color, title, text }) => (
            <div
              key={title}
              className="bg-white/70 backdrop-blur-lg p-8 rounded-3xl shadow-lg"
            >
              <Icon className={`${color} w-10 h-10 mb-4`} />
              <h2 className="text-2xl font-semibold mb-3">{title}</h2>
              <p className="text-gray-600">{text}</p>
            </div>
          ))}
        </div>

        <div className="space-y-8">
          {sections.map((section) => (
            <article
              key={section.title}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-10"
            >
              <h2 className="text-3xl font-bold mb-5 text-slate-900">
                {section.title}
              </h2>
              <div className="space-y-4 text-gray-700 leading-8">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="bg-red-50 p-10 rounded-3xl">
          <h2 className="text-3xl font-bold mb-4 text-red-600">
            Compliance Commitment
          </h2>
          <p className="text-gray-700 max-w-4xl leading-8">
            Our company follows international AML standards and continuously
            improves internal systems and monitoring practices to detect and
            prevent financial crime, fraud, and abuse.
          </p>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
}

export default AmlPolicy;
